/**
 * Shadow of types and enums from `roboto.query.api`
 */

import { TargetEntityType } from "@/shared/domain/customFields";
import { ISO_8601_DATETIME } from "@/types";
import { SearchQueryBody } from "@/types/search";

export enum QueryTarget {
  Collections = "collections",
  Datasets = "datasets",
  Devices = "devices",
  Events = "events",
  Files = "files",
  Sessions = "sessions",
  Topics = "topics",
  TopicMessagePaths = "topic_message_paths",
}

export function queryTargetToDisplayName(target: QueryTarget) {
  if (target === QueryTarget.TopicMessagePaths) {
    return "Message Paths";
  }

  return target;
}

/**
 * Maps a query target to the custom-field `TargetEntityType` whose Ready
 * fields the RoboQL editor should offer, or `undefined` for targets without
 * custom-field search+sort.
 */
export function queryTargetToEntityType(
  target: QueryTarget,
): TargetEntityType | undefined {
  switch (target) {
    case QueryTarget.Datasets:
      return TargetEntityType.Dataset;
    case QueryTarget.Events:
      return TargetEntityType.Event;
    default:
      return undefined;
  }
}

export enum QueryStatus {
  Failed = "failed",
  ResultsAvailable = "results_available",
  Scheduled = "scheduled",
}

export enum QueryScheme {
  QuerySpecV1 = "query_spec_v1",
}

export enum QueryContentMode {
  RecordWithMeta = "record_with_meta",
  RecordOnly = "record_only",
}

export interface QueryRecord {
  modified: ISO_8601_DATETIME;
  org_id: string;
  query_id: string;
  query_ctx: {
    query_scheme: QueryScheme;
    query: Record<string, unknown>;
  };
  status: QueryStatus;
  submitted: ISO_8601_DATETIME;
  target: QueryTarget;
}

export function getQueryContentMode(record: QueryRecord): QueryContentMode {
  const contentMode = record.query_ctx.query?.["content_mode"];

  if (
    contentMode === undefined ||
    contentMode === null ||
    !(typeof contentMode === "string")
  ) {
    return QueryContentMode.RecordWithMeta;
  }

  return contentMode as QueryContentMode;
}

export interface QualifiedRoboqlQuery {
  query: string;
  target: QueryTarget;
}

/**
 * The most values one value count returns, and the largest `limit` a
 * `ValueCountsSpec` may ask for: the server's
 * `roboto.query.MAX_VALUE_COUNTS_LIMIT`.
 */
export const MAX_VALUE_COUNTS_LIMIT = 1000;

/**
 * Experimental: this request shape may change or be removed without notice.
 * How many of the records a submitted query matches hold each value of one
 * field, read with `GET v1/query/id/{id}/value-counts`. The route serves the
 * events, sessions, datasets, devices, collections, and files targets.
 * `field` is a field the target sorts by (under its name or an alias), or a
 * Ready custom field spelled `custom.<name>`. The route answers 400 for a
 * target it does not serve, a field the target cannot count, or a
 * `time_field` that is not one of the target's time fields.
 */
export interface ValueCountsSpec {
  field: string;
  /** The most values returned, most common first; at most
   *  `MAX_VALUE_COUNTS_LIMIT`, which is also the default. */
  limit?: number;
  /** A time field of the target, such as `start_time` or `created`. Each
   *  value then carries the earliest and latest time among the records
   *  holding it. */
  time_field?: string;
}

/**
 * Experimental: this result shape may change or be removed without notice.
 * One value of the counted field and how many matching records hold it.
 * `value` has the field's JSON type: a string, number or boolean. An audit
 * time (`created`, `modified`) or a custom timestamp field is an ISO 8601
 * string, and a recorded time (a session's `min_timestamp_ns`, an event's
 * `start_time`) is integer nanoseconds since the Unix epoch.
 * `QueryService.getValueCounts` reads an integer `value`, `first_time` or
 * `last_time` as a `bigint`.
 */
export interface ValueCount {
  value: unknown;
  count: number;
  first_time?: bigint | string | null;
  last_time?: bigint | string | null;
}

/**
 * Experimental: this result shape may change or be removed without notice.
 * The value-counts route's result. `values` holds the most common values,
 * ties ordered by value ascending, and never the records without a value,
 * which `without_value` counts. `matched`, `without_value` and
 * `distinct_values` cover every matching record, whatever the spec's
 * `limit`; `truncated` is true exactly when `distinct_values` exceeds the
 * values returned.
 */
export interface ValueCounts {
  field: string;
  matched: number;
  without_value: number;
  distinct_values: number;
  values: ValueCount[];
  truncated: boolean;
}

export interface SubmitStructuredQueryRequest {
  content_mode: QueryContentMode;
  query: SearchQueryBody;
  target: QueryTarget;
  value_counts?: ValueCountsSpec;
}

export interface SubmitRoboqlQueryRequest {
  content_mode: QueryContentMode;
  query: string;
  target: QueryTarget;
}

export interface SubmitTermQueryRequest {
  content_mode: QueryContentMode;
  term: string;
  target: QueryTarget;
}

export interface MetadataValuesRequest {
  target: QueryTarget;
  metadata_key: string;
  entity_ids: string[];
}

export interface MetadataValuesResponse {
  metadata_key: string;
  metadata_values: Record<string, unknown>;
}
